import type { Profile, PresetProfile, CallResult, NetworkProbeResult, AnalysisRequest, ChatRequest, BenchmarkRun } from '@/types'
import { getActiveStrategy, getExecutionMode } from './probe-strategy'
import { sanitizeForExport } from './security-utils'
import { resultsStore } from './results-store'
import { loadLocalPresets } from './preset-loader'
import { localRequestAnalysis, localSendChatMessage } from './local-analysis-client'
import { getJwtToken, isCognitoConfigured } from './cognito-auth'
import { useAppStore } from '@/store/app-store'
import { hasFolderAccess, saveResultToFolder } from './fs-storage'
import { API_BASE } from '@/config/api'

/**
 * Pure function: builds the Authorization header from a JWT token.
 * Returns `{ Authorization: 'Bearer {token}' }` when token is non-null,
 * empty object when null.
 */
export function buildAuthHeader(token: string | null): Record<string, string> {
  if (token) {
    return { Authorization: `Bearer ${token}` }
  }
  return {}
}

/**
 * Builds headers for cloud API requests, including JWT Authorization
 * when a Cognito token is available.
 */
export async function buildCloudHeaders(): Promise<Record<string, string>> {
  const token = await getJwtToken()
  return {
    'Content-Type': 'application/json',
    ...buildAuthHeader(token),
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const headers = await buildCloudHeaders()
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`API error ${res.status}: ${text}`)
  }
  return res.json()
}

export interface ProbeRequest {
  profile: Profile | PresetProfile
  callId: number
}

export interface ProbeResponse {
  result: CallResult
}

export interface NetworkProbeRequest {
  host: string
  proxyUrl?: string
}

/**
 * Builds the probe request body, adapting for preset vs custom profiles.
 * For preset profiles: sends presetName, prompt, maxTokens, name, id, type only.
 * For custom profiles: sends the full profile object unchanged.
 */
export function buildProbeRequestBody(req: ProbeRequest): { profile: unknown; callId: number } {
  const { profile, callId } = req
  if ('isPreset' in profile && profile.isPreset) {
    return {
      profile: {
        presetName: profile.presetName,
        prompt: profile.prompt,
        maxTokens: profile.maxTokens,
        name: profile.name,
        id: profile.id,
        type: 'bedrock' as const,
        ...(profile.modelId ? { modelId: profile.modelId } : {}),
        ...(profile.region ? { region: profile.region } : {}),
        ...(profile.guardrailId ? { guardrailId: profile.guardrailId, guardrailVersion: profile.guardrailVersion } : {}),
      },
      callId,
    }
  }
  // For custom profiles, include imageAttachments if present
  const p = profile as Profile
  const body: Record<string, unknown> = { ...p }
  if (p.imageAttachments && p.imageAttachments.length > 0) {
    body.imageAttachments = p.imageAttachments
  }
  return { profile: body, callId }
}

/**
 * Delegates to the active probe strategy (Cloud or Local) based on execution mode.
 * In Cloud mode: sends request to API Gateway/backend.
 * In Local mode: invokes Bedrock/Anthropic/Grok directly from the browser.
 */
export async function runProbe(req: ProbeRequest): Promise<CallResult> {
  const strategy = getActiveStrategy()
  return strategy.runProbe(req)
}

/**
 * Delegates to the active probe strategy for network probes.
 * In Local mode, returns a stub since browser cannot do TCP/TLS timing.
 */
export async function runNetworkProbe(req: NetworkProbeRequest): Promise<NetworkProbeResult> {
  const strategy = getActiveStrategy()
  return strategy.runNetworkProbe(req)
}

/**
 * Fetches preset profiles from the backend (Cloud) or bundled config (Local).
 * Returns an empty array on error or non-200 response.
 */
export async function fetchPresets(): Promise<PresetProfile[]> {
  // Always start with bundled presets (works offline, no auth needed)
  const local = loadLocalPresets()

  // If authenticated, also try to restore saved preset state from the backend
  const { useAppStore } = await import('@/store/app-store')
  const { isAuthenticated } = useAppStore.getState()
  if (isAuthenticated) {
    try {
      const headers = await buildCloudHeaders()
      const res = await fetch(`${API_BASE}/presets`, { headers })
      if (res.ok) {
        const data = await res.json()
        if (Array.isArray(data) && data.length > 0) {
          return data.map((item: any) => ({
            id: `preset-${item.name}`,
            name: item.name,
            presetName: item.name,
            defaultPrompt: item.defaultPrompt,
            defaultMaxTokens: item.defaultMaxTokens,
            complexity: item.complexity,
            profileType: item.profileType || '',
            prompt: item.defaultPrompt,
            maxTokens: item.defaultMaxTokens,
            enabled: false,
            isPreset: true as const,
            ...(item.guardrailId ? { guardrailId: item.guardrailId, guardrailVersion: item.guardrailVersion } : {}),
          }))
        }
      }
    } catch { /* fall through to local */ }
  }

  return local
}


/**
 * Sends benchmark results to the analysis endpoint (Cloud) or Bedrock Converse (Local).
 */
export async function requestAnalysis(req: AnalysisRequest): Promise<string> {
  if (getExecutionMode() === 'local') {
    return localRequestAnalysis(req)
  }
  const headers = await buildCloudHeaders()
  const res = await fetch(`${API_BASE}/analyze`, {
    method: 'POST',
    headers,
    body: JSON.stringify(req)
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(data.error || `API error ${res.status}`)
  }
  const data = await res.json()
  return data.analysis
}



/**
 * Sends a chat message with full benchmark context for multi-turn Q&A.
 * Routes to Bedrock Converse in Local Mode.
 */
export async function sendChatMessage(req: ChatRequest): Promise<string> {
  if (getExecutionMode() === 'local') {
    return localSendChatMessage(req)
  }
  const headers = await buildCloudHeaders()
  const res = await fetch(`${API_BASE}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify(req)
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(data.error || `API error ${res.status}`)
  }
  const data = await res.json()
  return data.reply
}


/**
 * Saves benchmark results to S3/filesystem via the backend (Cloud) or IndexedDB (Local).
 * In local mode, also sends results to the backend for DynamoDB storage when
 * cloud storage is enabled and the user is authenticated via Cognito.
 * Strips image attachments from profiles before saving.
 */
export async function saveResults(run: BenchmarkRun): Promise<{ saved: boolean; key: string }> {
  if (getExecutionMode() === 'local') {
    // Always save to IndexedDB in local mode
    await resultsStore.saveRun(run)

    // Optionally save to local folder via File System Access API
    const { localFolderEnabled } = useAppStore.getState()
    if (localFolderEnabled && hasFolderAccess()) {
      try {
        const cleanRun = stripImageAttachments(run)
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        const filename = `benchmark-${run.id}-${timestamp}.json`
        await saveResultToFolder(filename, cleanRun)
      } catch (err) {
        console.warn('[api-client] Local folder save failed:', err)
        // Non-fatal - result is already in IndexedDB
      }
    }

    // Optionally also persist to cloud (DynamoDB) if authenticated + toggle enabled
    const { isAuthenticated, cloudStorageEnabled } = useAppStore.getState()
    if (cloudStorageEnabled && isAuthenticated && isCognitoConfigured()) {
      try {
        const cleanRun = stripImageAttachments(run)
        const sanitizedRun = sanitizeForExport(cleanRun)
        await post('/save-results', { runId: run.id, data: sanitizedRun, visibility: 'private' })
      } catch (err) {
        console.warn('[api-client] Cloud persistence failed for local mode result:', err)
        // Non-fatal - result is already in IndexedDB
      }
    }

    return { saved: true, key: run.id }
  }
  // Cloud mode: send to backend
  const cleanRun = stripImageAttachments(run)
  const sanitizedRun = sanitizeForExport(cleanRun)
  return post<{ saved: boolean; key: string }>('/save-results', { runId: run.id, data: sanitizedRun, visibility: 'public' })
}

/**
 * Strips image attachments from profiles to keep payloads small.
 */
function stripImageAttachments(run: BenchmarkRun): BenchmarkRun {
  return {
    ...run,
    profiles: run.profiles.map(p => {
      if ('imageAttachments' in p) {
        const { imageAttachments, ...rest } = p as any
        return { ...rest, imageCount: imageAttachments?.length || 0 }
      }
      return p
    })
  }
}
